import { create } from "zustand";
import type { SplitGroup, SplitGroupMember } from "@boo/shared";
import {
  getSplitGroups,
  createSplitGroup,
  updateSplitGroup,
  deleteSplitGroup,
  addSplitGroupMember,
  removeSplitGroupMember,
  type CreateSplitGroupPayload,
  type SplitGroupMemberPayload,
  type UpdateSplitGroupPayload,
} from "../services/expenseApi";

interface SplitGroupState {
  groups: SplitGroup[];
  loading: boolean;
  loadGroups: () => Promise<void>;
  createGroup: (name: string) => Promise<SplitGroup>;
  updateGroup: (id: string, patch: UpdateSplitGroupPayload) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  addMember: (
    groupId: string,
    payload: SplitGroupMemberPayload,
  ) => Promise<SplitGroupMember>;
  removeMember: (groupId: string, memberId: string) => Promise<void>;
}

export const useSplitGroupStore = create<SplitGroupState>((set) => ({
  groups: [],
  loading: false,

  async loadGroups() {
    set({ loading: true });
    try {
      const groups = await getSplitGroups();
      set({ groups });
    } finally {
      set({ loading: false });
    }
  },

  async createGroup(name: string) {
    const payload: CreateSplitGroupPayload = { name };
    const group = await createSplitGroup(payload);
    set((s) => ({ groups: [...s.groups, group] }));
    return group;
  },

  async updateGroup(id: string, patch: UpdateSplitGroupPayload) {
    const updated = await updateSplitGroup(id, patch);
    set((s) => ({
      groups: s.groups.map((g) =>
        g.id === id
          ? {
              ...g,
              name: updated.name,
              tone: updated.tone,
              shareMessageOpen: updated.shareMessageOpen,
              shareMessageFinal: updated.shareMessageFinal,
            }
          : g,
      ),
    }));
  },

  async deleteGroup(id: string) {
    await deleteSplitGroup(id);
    set((s) => ({ groups: s.groups.filter((g) => g.id !== id) }));
  },

  async addMember(groupId: string, payload: SplitGroupMemberPayload) {
    const member = await addSplitGroupMember(groupId, payload);
    set((s) => ({
      groups: s.groups.map((g) =>
        g.id === groupId ? { ...g, members: [...g.members, member] } : g,
      ),
    }));
    return member;
  },

  async removeMember(groupId: string, memberId: string) {
    await removeSplitGroupMember(groupId, memberId);
    set((s) => ({
      groups: s.groups.map((g) =>
        g.id === groupId
          ? { ...g, members: g.members.filter((m) => m.id !== memberId) }
          : g,
      ),
    }));
  },
}));
